import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { hydrateReadingFromBackend } from '../utils/readingPoints';
import { identify as analyticsIdentify, resetIdentity as analyticsReset } from '../lib/analytics';
import { loadStoredToken, saveToken, clearToken, getTokenSync, loadStoredAvatar, saveAvatar, clearAvatar, getAvatarSync, loadStoredAgentCover, loadStoredAgentPhoto, clearAgentCover, clearAgentPhoto, saveRecentEmail, loadStoredUserSnapshot, saveUserSnapshot, clearUserSnapshot, getUserSnapshotSync } from '../lib/nativeSession';
import { markSessionStart, markLoginSuccess, maybeRequestReview } from '../lib/appReview';
import { openExternalAuth, metaCompletedRegistration, clearMediaHubWidgetAthlete, setMediaHubWidgetAthlete } from '../lib/nativeBridge';
import { trackMeta } from '../lib/metaTracking';
import { journeyAttempt, journey } from '../lib/journey';
import { readReferralCode, clearReferralCode, stashReferralCode } from '../utils/referralCode';
// iter885 · An app Google sign-in waiting to be claimed ({ state, at }).
// Kept in localStorage so coming back to FLYT always finishes the
// sign-in, even if the in-flight poll was cut off.
const PENDING_GOOGLE_KEY = 'flyt_pending_google';
let _lastGoogleTap = 0;
const readPendingGoogle = () => {
  try { return JSON.parse(window.localStorage.getItem(PENDING_GOOGLE_KEY) || 'null'); } catch { return null; }
};
const clearPendingGoogle = (state) => {
  try {
    if (!state || readPendingGoogle()?.state === state) window.localStorage.removeItem(PENDING_GOOGLE_KEY);
  } catch { /* ignore */ }
};
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const UserAuthContext = createContext(null);
// Standalone axios instance — same withCredentials default (set globally in
// index.js), but isolated baseURL so this context doesn't accidentally pick
// up the admin auth flow's interceptors.
const userApi = axios.create({ baseURL: API, withCredentials: true });
// iOS Capacitor: attach the persisted session token as a Bearer header so
// the backend can authenticate this request when the cross-origin cookie
// gets dropped. No-ops on web (token is null there).
userApi.interceptors.request.use((config) => {
  const t = getTokenSync();
  if (t && !config.headers?.Authorization) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});
// Whenever an auth response carries a fresh `session_token`, store it so
// the next request picks it up automatically. Web ignores this; iOS uses
// it as the primary auth source.
//
// iter310 · Critical: the interceptor `then`-callback MUST await the
// Preferences write, otherwise iOS can suspend the JS runtime the moment
// the response settles (e.g. user swipes the app away right after
// Google finishes) and the token never lands on disk. Returning a
// Promise from a response interceptor is a supported axios feature —
// downstream `.then` handlers keep waiting until we resolve, so the
// save has a real chance to commit.
userApi.interceptors.response.use(async (res) => {
  const tok = res?.data?.session_token;
  if (tok) {
    try { await saveToken(tok); } catch { /* best-effort */ }
  }
  return res;
});
// Kick off a one-time hydration of the stored token (iOS only). Resolves
// before the first /me call below thanks to React's effect-ordering.
loadStoredToken();
export const UserAuthProvider = ({ children }) => {
  // iter445 · Seed initial state from the sync-primed localStorage
  // snapshot (see nativeSession.js top-of-module prime block). If the
  // user was authed on the previous launch we start with THEIR user
  // object in state and `loading=false` — React's very first paint
  // renders the authed UI, no flash of the logged-out home. The
  // background refreshUser() below still reconciles with the server
  // and paints any diffs on the next tick.
  const _bootSnapshot = getUserSnapshotSync();
  const [user, setUser] = useState(_bootSnapshot);
  const [loading, setLoading] = useState(!_bootSnapshot);
  const [loginOpen, setLoginOpen] = useState(false);
  // iter674 · Claim-code sheet — lets a Google/Apple/email user manually
  // connect a pre-built mockup profile Jay DM'd them (ProfilePage has the
  // "Claim your profile" entry point). No longer auto-opened after every
  // login/signup — it used to fire for virtually every brand-new account
  // (persona isn't 'athlete' and athlete_slug isn't set until moments
  // later, when PersonaWelcomeModal/onboarding actually run), popping up
  // mid-flow and looking like a second, broken signup step.
  const [claimSheetOpen, setClaimSheetOpen] = useState(false);
  // iter243 · Which panel the LoginModal opens on ('login' | 'signup').
  // openSignup() lands visitors straight on the create-account form —
  // used by the landing-page "Claim your profile" CTAs.
  const [loginMode, setLoginMode] = useState('login');
  const openSignup = useCallback(() => { setLoginMode('signup'); setLoginOpen(true); }, []);
  // iter177 · Monotonic counter that bumps on every follow/save toggle.
  // Consumers (e.g. ProfilePage) include it in a useEffect dep array
  // so their derived state (Following grid, Saved count) refetches the
  // moment ANY tap flips the follow/save bucket, regardless of where
  // in the tree that tap happened.
  const [savedVersion, setSavedVersion] = useState(0);
  const refreshUser = useCallback(async () => {
    try {
      // validateStatus accepts 401 so axios doesn't spam the console for
      // anonymous visitors — 401 just means "not logged in yet", not an error.
      let res = await userApi.get('/me', { validateStatus: (s) => s === 200 || s === 401 });
      if (res.status === 401) {
        // A 401 on the FIRST try, immediately after a just-completed
        // sign-in, is almost always a transient session-propagation blip
        // (cookie not fully committed yet, a split-second race on the
        // backend) — not a real sign-out. AthleteOnboardingFlow's
        // exitToApp() calls refreshUser() right after finishing the
        // entire signup+onboarding flow specifically to pick up the
        // athlete's freshly-saved sport; trusting a bare 401 there wiped
        // out a perfectly valid session and dumped the user back on the
        // signup form. One retry after a short pause distinguishes a real
        // sign-out from this blip everywhere refreshUser is called.
        await new Promise((r) => setTimeout(r, 500));
        res = await userApi.get('/me', { validateStatus: (s) => s === 200 || s === 401 });
      }
      if (res.status === 200) {
        // iter303/iter333 · Native iOS heal — Capacitor Preferences
        // hold the most recent successful upload from THIS device. If
        // the local mirror differs from what the server returned
        // (either server is empty because a fire-and-forget PUT was
        // lost when iOS suspended the runtime, OR server still holds a
        // stale pre-replacement picture), prefer the local copy and
        // fire a heal-PUT to /me/profile so the server catches up.
        // Fixes the TestFlight "picture reverts to old one on relaunch"
        // bug reported by the founder Aug 2 without changing anything
        // for the web app (getAvatarSync is null there). The local
        // mirror is cleared on logout so a new sign-in never inherits
        // a stale picture from a previous session.
        const localAvatar = getAvatarSync();
        let hydrated = res.data;
        if (localAvatar && hydrated && localAvatar !== hydrated.picture) {
          hydrated = { ...hydrated, picture: localAvatar };
          try {
            userApi.put('/me/profile', { picture: localAvatar }).catch(() => {});
          } catch { /* ignore */ }
        }
        // iter535 · Claim-approval instant-refresh — if the athlete
        // just got verified on the server (either via founder approval
        // in the admin queue OR because /me was polled right after
        // an APNs "claim approved" push landed), we detect the flip
        // here and purge every cached read that could show them a
        // stale "UNCLAIMED · Claim & Verify" pill. Also dispatches a
        // `flyt:claim-approved` window event so any currently-mounted
        // AthleteNetworkPage can force-refetch in-place, closing the
        // gap where the walkthrough appears but the profile pill
        // still says UNCLAIMED for ~2 minutes.
        try {
          const wasVerified = !!(user && (user.is_verified_athlete || user.linked_athlete?.claim_status === 'verified'));
          const isVerifiedNow = !!(hydrated?.is_verified_athlete || hydrated?.linked_athlete?.claim_status === 'verified');
          const slug = hydrated?.linked_athlete?.slug || null;
          if (!wasVerified && isVerifiedNow) {
            // Purge every SWR entry that touches this athlete so the
            // next paint reads fresh from the server.
            const PREFIX = 'flyt:swr:';
            const toDrop = [];
            for (let i = 0; i < window.localStorage.length; i++) {
              const k = window.localStorage.key(i);
              if (!k || !k.startsWith(PREFIX)) continue;
              const raw = k.slice(PREFIX.length);
              const touchesAthlete =
                raw.startsWith('/api/network/athletes')
                || raw.startsWith('/api/athletes')
                || raw.startsWith('/api/me')
                || raw.startsWith('/api/featured-reel')
                || raw.startsWith('/api/network/')
                || (slug && raw.includes(`/${slug}`));
              if (touchesAthlete) toDrop.push(k);
            }
            for (const k of toDrop) window.localStorage.removeItem(k);
            // Broadcast so any live AthleteNetworkPage can refetch NOW.
            window.dispatchEvent(new CustomEvent('flyt:claim-approved', {
              detail: { slug, user_id: hydrated?.user_id },
            }));
          }
        } catch { /* localStorage / SSR — non-fatal */ }
        setUser(hydrated);
        // iter827 · Jay: "I'm already logged into the app... it's still
        // not letting me do anything" — the widget-linking call used to
        // live ONLY in AthleteMePage.js's data-load effect, but that's
        // not the screen logged-in athletes actually land on (HomeIndex
        // in App.js routes them to AthleteHome instead) — so most people
        // never visited the one page that wrote their slug into the
        // shared App Group container, and the widget stayed stuck on
        // "Log in to connect your Media Hub" no matter how many times
        // they opened the app. Doing it right here — every time /me
        // resolves, on every cold boot, regardless of which screen loads
        // next — is what actually makes this zero-configuration.
        if (hydrated?.linked_athlete?.slug) setMediaHubWidgetAthlete(hydrated.linked_athlete.slug);
        // iter358 · Persist the successful /me payload so the next
        // cold boot renders the authed UI instantly (before the
        // network round-trip completes). See loadStoredUserSnapshot
        // in the boot useEffect for the read side.
        saveUserSnapshot(hydrated).catch(() => {});
        // iter537 · ATT prompt removed. FLYT House does no tracking under
        // Apple's ATT definition (no ad SDKs, no data brokers, no cross-
        // app linking) so prompting for authorization we don't use was
        // just extra friction — and some reviewers flag calling
        // `requestTrackingAuthorization` when nothing depends on it.
        // App Privacy labels now correctly report "Data Not Used to
        // Track You" so the prompt is intentionally absent.
        return hydrated;
      }
      setUser(null);
      return null;
    } catch {
      setUser(null);
      return null;
    }
  }, []);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // iOS: the Bearer token lives in Capacitor Preferences (async native
      // read). MUST resolve before the first /me call or the request goes
      // out unauthenticated and the user looks logged-out on every launch.
      // The avatar backup uses the same store — pre-load both in parallel
      // so refreshUser can consult them synchronously.
      await Promise.all([loadStoredToken(), loadStoredAvatar(), loadStoredAgentCover(), loadStoredAgentPhoto(), loadStoredUserSnapshot()]);
      // iter358 · Stale-while-revalidate — if we have a cached user
      // snapshot from a previous session, paint the authed UI IMMEDIATELY
      // while /me revalidates in the background. Cuts perceived cold-boot
      // time from ~800ms (network) to <50ms (disk read).
      const snap = getUserSnapshotSync();
      if (snap && snap.user_id && !cancelled) {
        setUser(snap);
        setLoading(false);          // let the UI render before /me returns
      }
      // iter377 · Stamp session start once per app boot — feeds the
      // 3-minute guardrail on maybeRequestReview() further down.
      markSessionStart();
      await refreshUser();
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [refreshUser]);
  // When a public user signs in (or a session is restored on page reload),
  // sync any guest reading-points progress to the backend and pull the
  // authoritative state down so the same points/completed/reward show up
  // on every device the user logs into.
  useEffect(() => {
    if (!user?.user_id) return;
    hydrateReadingFromBackend(user.user_id);
  }, [user?.user_id]);
  // Sync PostHog identity whenever the user state flips. Identifies on
  // sign-in (with stable user_id), resets on sign-out so the next visitor
  // isn't accidentally merged into the previous person.
  useEffect(() => {
    if (user?.user_id) {
      analyticsIdentify(user.user_id, {
        email: user.email,
        name: user.name,
        persona: user.persona,
        signup_channel: user.signup_channel,
      });
      // iter353 · Only touch RevenueCat once the SDK key env var is
      // present AND we're on native iOS. Without the key, the dynamic
      // import still parses ~200KB of RC SDK code inside the WKWebView
      // AND the native bridge blocks on Purchases.configure until it
      // times out looking for the (not-yet-installed) native plugin.
      // Guard here so web + pre-IAP-launch iOS builds never pay for it.
      if (
        typeof process !== 'undefined' &&
        process.env.REACT_APP_REVENUECAT_IOS_KEY &&
        window.Capacitor?.getPlatform?.() === 'ios' &&
        window.Capacitor?.isPluginAvailable?.('Purchases')
      ) {
        (async () => {
          try {
            const { configureRevenueCat, syncCurrentEntitlement } = await import('../lib/revenuecat');
            await configureRevenueCat(user.user_id);
            // iter766 · Self-heal any existing account/webhook mismatch
            // on every launch — see the comment on syncCurrentEntitlement.
            syncCurrentEntitlement();
          } catch { /* SDK missing on this build — noop */ }
        })();
      }
    } else if (!loading) {
      analyticsReset();
    }
  }, [user?.user_id, user?.email, user?.name, user?.persona, user?.signup_channel, loading]);
  // iter377 · App Store review prompt — checks its own guardrails
  // (native iOS + ≥3 lifetime logins + ≥3-minute session + ≤4
  // lifetime prompts + ≥30 days since last prompt) before invoking
  // SKStoreReviewController. Fires once ~3 minutes after the user
  // lands in the authenticated app, then once every 5 minutes while
  // the app stays open — the plugin itself will silently no-op past
  // the guardrails so the user never sees the sheet twice per boot.
  useEffect(() => {
    if (!user?.user_id) return;
    let cancelled = false;
    const kick = () => { if (!cancelled) maybeRequestReview(); };
    const first = setTimeout(kick, 3 * 60 * 1000 + 2000);  // just past the 3-min mark
    const interval = setInterval(kick, 5 * 60 * 1000);
    return () => { cancelled = true; clearTimeout(first); clearInterval(interval); };
  }, [user?.user_id]);
  // Public: kick off Emergent Google sign-in.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  //
  // Web: redirect URL = current origin + returnPath (works because Emergent
  // Auth redirects back to that URL and the browser handles the fragment).
  //
  // iOS Capacitor: the WKWebView origin is `capacitor://localhost`, which
  // Safari cannot open when Google → Emergent Auth redirects back. So we
  // send a REAL HTTPS callback URL (flythouse.com/auth/callback) — Universal
  // Links (see apple-app-site-association) intercept that redirect and
  // hand it back to the app via `bindUniversalLinks()` in nativeBridge.
  // iter328 · startAppleLogin — Sign in with Apple. Lazily loads
  // AppleID.auth.js on click, initializes with Service ID + redirect
  // URI pulled from backend config, calls signIn(), sends the returned
  // id_token to POST /auth/apple/session which verifies via JWKS and
  // drops the session cookie. Works in web + iOS WebView.
  const startAppleLogin = useCallback(async (returnPath = '/profile') => {
    // iter880 · App Health journey tracking.
    const j = journeyAttempt('login_apple');
    try {
      const cfgRes = await userApi.get('/auth/apple/config');
      const cfg = cfgRes.data || {};
      if (!cfg.enabled) {
        j.fail(new Error('Sign in with Apple not configured on the backend'));
        toast.error('Sign in with Apple isn\u2019t configured yet. Try Google or email.');
        return;
      }
      // iter358 · Native iOS MUST use ASAuthorizationController via the
      // @capacitor-community/apple-sign-in plugin. The Apple JS SDK's
      // popup mode (below) doesn't work inside Capacitor WKWebView —
      // popups either fail to open or can't post a message back to the
      // opener, which is why users were seeing "Apple sign-in failed.
      // Try again." on native. On web the plugin isn't available and
      // we fall through to the original AppleID.auth flow.
      if (typeof window !== 'undefined' && window?.Capacitor?.isNativePlatform?.()) {
        try {
          const { isNativeAppleAuthAvailable, signInWithAppleNative } = await import('../lib/nativeAppleAuth');
          if (isNativeAppleAuthAvailable()) {
            // iter358 · Native iOS ASAuthorizationController expects the
            // Bundle ID as clientId (not the web Service ID). Backend
            // returns both — we prefer bundle_id here, fall back to
            // service_id for older backend deploys, and finally to the
            // hardcoded launch bundle so this can never dead-end.
            const nativeClientId = cfg.bundle_id || cfg.service_id || 'com.flytstudios.league';
            const { idToken, name: nativeName } = await signInWithAppleNative({
              clientId: nativeClientId,
              redirectURI: cfg.redirect_uri,
            });
            const res = await userApi.post('/auth/apple/session', {
              id_token: idToken,
              name: nativeName || undefined,
              signup_channel: 'league',
            });
            if (res?.data?.session_token) {
              try { await saveToken(res.data.session_token); } catch { /* ignore */ }
            }
            if (res.data?.user) {
              setUser(res.data.user);
              j.ok();
              setLoginOpen(false);
              toast.success('Signed in with Apple');
              markLoginSuccess().catch(() => {});
              if (res.data?.is_new_user) { metaCompletedRegistration('apple'); trackMeta('CompleteRegistration', { email: res.data?.user?.email, customData: { method: 'apple', status: 'complete' } }); }
              if (typeof window !== 'undefined' && returnPath) window.location.assign(returnPath);
            }
            if (!res.data?.user) j.fail(new Error('Apple session returned no user'));
            return;
          }
        } catch (nativeErr) {
          // User cancelled → silent return so we don't nag them.
          const code = String(nativeErr?.code || nativeErr?.error || '').toLowerCase();
          const msg  = String(nativeErr?.message || '').toLowerCase();
          if (code.includes('cancel') || msg.includes('cancel') || msg.includes('1001')) { j.cancel(); return; }
          // Real failure → surface a clean error, don't fall through to
          // the JS SDK (which we know doesn't work in WKWebView anyway).
          throw new Error(nativeErr?.message || 'Apple sign-in failed. Try again.');
        }
      }
      // Web fallback — original AppleID JS SDK popup flow.
      // Lazy-load Apple JS SDK once per tab.
      if (!window.AppleID) {
        await new Promise((resolve, reject) => {
          const s = document.createElement('script');
          s.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
          s.async = true;
          s.onload = resolve;
          s.onerror = () => reject(new Error('Failed to load Apple SDK'));
          document.head.appendChild(s);
        });
      }
      window.AppleID.auth.init({
        clientId: cfg.service_id,
        scope: 'name email',
        redirectURI: cfg.redirect_uri,
        usePopup: true,
      });
      const data = await window.AppleID.auth.signIn();
      const id_token = data?.authorization?.id_token;
      if (!id_token) throw new Error('Apple returned no id_token');
      // Apple only sends `user.name` on first auth — pass it through.
      const nameObj = data?.user?.name || null;
      const fullName = nameObj ? [nameObj.firstName, nameObj.lastName].filter(Boolean).join(' ').trim() : '';
      const res = await userApi.post('/auth/apple/session', {
        id_token,
        name: fullName || undefined,
        signup_channel: 'league',
      });
      if (res.data?.user) {
        setUser(res.data.user);
        j.ok();
        setLoginOpen(false);
        toast.success('Signed in with Apple');
        if (res.data?.is_new_user) { metaCompletedRegistration('apple'); trackMeta('CompleteRegistration', { email: res.data?.user?.email, customData: { method: 'apple', status: 'complete' } }); }
        if (typeof window !== 'undefined' && returnPath) window.location.assign(returnPath);
      }
    } catch (err) {
      if (err?.error === 'popup_closed_by_user' || err?.code === 'user_cancelled_authorize'
          || String(err?.message || '').includes('1001') || String(err?.message || '').toLowerCase().includes('cancel')) { j.cancel(); return; }
      j.fail(err);
      const msg = err?.response?.data?.detail || err?.message || 'Apple sign-in failed. Try again.';
      // iter305 · No local toast here — the caller (LoginModal's
      // `runProviderLogin`) already renders both the inline error and
      // a single toast. Emitting one here as well produced duplicate
      // Sonner toasts on non-cancel failures.
      throw new Error(String(msg));
    }
  }, []);
  // startGoogleLogin — kicks off the Google OAuth consent flow.
  //
  // iter119 · When GOOGLE_OAUTH_CLIENT_ID is configured on the backend
  // we send users STRAIGHT to Google's OAuth consent screen with our
  // own client_id — the popup shows "Flythouse" as the app name
  // instead of "auth.emergentagent.com". If the custom client isn't
  // configured, we transparently fall back to the Emergent-managed
  // flow (which still works — just shows the Emergent branding).
  //
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
  // URLS, THIS BREAKS THE AUTH.
  // iter885 · Claim an app Google sign-in's one-time handoff and sign the
  // app in. Used by the poll in startGoogleLogin AND whenever FLYT comes
  // back to the foreground, so a sign-in that finished in Safari still
  // lands. Returns true when this call signed the app in.
  const claimGoogleHandoff = useCallback(async (state) => {
    if (!state) return false;
    const hr = await userApi.get(`/auth/handoff/${state}`, { validateStatus: (s) => s === 200 || s === 400 || s === 404 });
    if (hr.status !== 200 || !hr.data?.session_token) return false;
    clearPendingGoogle(state);
    try { await saveToken(hr.data.session_token); } catch { /* ignore */ }
    try {
      const { Browser } = await import('@capacitor/browser');
      Browser.close().catch(() => {});
    } catch { /* ignore */ }
    const me = await userApi.get('/me', { validateStatus: (s) => s === 200 || s === 401 });
    if (me.status === 200 && me.data?.user_id) {
      setUser(me.data);
      saveUserSnapshot(me.data).catch(() => {});
      if (me.data.email) saveRecentEmail(me.data.email).catch(() => {});
      markLoginSuccess().catch(() => {});
      setLoginOpen(false);
      await applyReferralAttribution();
    }
    return true;
  }, []);
  // iter885 · Back in FLYT (launch or foreground) with a Google sign-in
  // still pending → claim it. Covers the Safari-app path and any poll
  // that was interrupted.
  useEffect(() => {
    if (typeof window === 'undefined' || !window?.Capacitor?.isNativePlatform?.()) return undefined;
    const tryPending = () => {
      const pend = readPendingGoogle();
      if (!pend?.state) return;
      if (Date.now() - (pend.at || 0) > 10 * 60 * 1000) { clearPendingGoogle(pend.state); return; }
      claimGoogleHandoff(pend.state)
        .then((ok) => { if (ok) journey('login_google', 'ok', { attempt: pend.state }); })
        .catch(() => { /* network blip — next foreground retries */ });
    };
    tryPending();
    let handle = null;
    let gone = false;
    import('@capacitor/app')
      .then(({ App }) => App.addListener('appStateChange', ({ isActive }) => { if (isActive) tryPending(); }))
      .then((h) => { if (gone) { try { h?.remove(); } catch { /* ignore */ } } else { handle = h; } })
      .catch(() => { /* ignore */ });
    return () => { gone = true; try { handle?.remove(); } catch { /* ignore */ } };
  }, [claimGoogleHandoff]);
  const startGoogleLogin = useCallback(async (returnPath = '/profile') => {
    // Persist where the user came from so the /auth/google callback
    // can bounce them back after we finish the exchange.
    try {
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem('flyt_oauth_return', returnPath);
      }
    } catch { /* private mode — noop */ }
    // Native iOS: send the OAuth URL to SFSafariViewController via
    // @capacitor/browser so Google actually completes (it rejects
    // Capacitor webviews as "insecure browsers"). After OAuth, the
    // Emergent Auth service redirects to the callback URL which is
    // captured by our Universal Link handler in `bindUniversalLinks`
    // (see lib/nativeBridge.js).
    //
    // iter309 · Fallback chain so we never surface the raw env-var name
    // to a shipped user (previous behavior threw
    // "REACT_APP_IOS_OAUTH_CALLBACK_URL must be set for iOS native auth"
    // when the bundle was built without the var). Real production apps
    // always have the callback route at `<origin>/auth/callback`, so we
    // derive it from the same `getPublicOrigin()` helper that every
    // share/Stripe/canonical flow uses.
    if (typeof window !== 'undefined' && window?.Capacitor?.isNativePlatform?.()) {
      // iter379 · Native Google Sign-In via
      // @codetrix-studio/capacitor-google-auth — shows Google's native
      // "Choose an account" sheet with every Google account signed in
      // on the phone (matches the Scoutz reference screenshot Jay
      // shared). Falls through to SFSafariViewController below on any
      // failure (plugin missing on older builds, user hasn't set up
      // the reversed client ID in Info.plist yet, etc.).
      try {
        const { isNativeGoogleAuthAvailable, signInWithGoogleNative } = await import('../lib/nativeGoogleAuth');
        if (isNativeGoogleAuthAvailable()) {
          // Resolve the web client ID (backend's OAuth client — the id_token
          // audience we verify against). Cache-first so consecutive taps
          // don't wait on the network.
          let cfgLocal = null;
          try {
            const raw = window.sessionStorage.getItem('flyt_oauth_cfg');
            if (raw) cfgLocal = JSON.parse(raw);
          } catch { /* ignore */ }
          if (!cfgLocal?.client_id) {
            try {
              const r = await userApi.get('/auth/google/config');
              cfgLocal = r?.data || null;
              try { window.sessionStorage.setItem('flyt_oauth_cfg', JSON.stringify(cfgLocal)); } catch { /* ignore */ }
            } catch { /* ignore */ }
          }
          if (cfgLocal?.client_id) {
            const { default: inferSignupChannel } = await import('../utils/inferSignupChannel');
            const activeRef = readReferralCode();
            const { idToken } = await signInWithGoogleNative(cfgLocal.client_id);
            const res = await userApi.post('/auth/google/id-token', {
              id_token: idToken,
              signup_channel: inferSignupChannel(),
              ref_code: activeRef,
            });
            if (res?.data?.session_token) {
              try { await saveToken(res.data.session_token); } catch { /* ignore */ }
            }
            setUser(res.data.user);
            // iter724 · Same fix as exchangeSession/exchangeGoogleCode —
            // this native "Choose an account" path is likely the one
            // actually running on a real device (checked first, before
            // the Safari-redirect fallback), so it had the exact same
            // "STEP INSIDE stuck open behind everything" bug.
            setLoginOpen(false);
            if (res?.data?.user?.email) saveRecentEmail(res.data.user.email).catch(() => {});
            markLoginSuccess().catch(() => {});
            if (res.data?.is_new_user) { metaCompletedRegistration('google'); trackMeta('CompleteRegistration', { email: res.data?.user?.email, customData: { method: 'google', status: 'complete' } }); }
            await applyReferralAttribution();
            return;
          }
        }
      } catch (err) {
        // Any of these paths failing — plugin missing, user cancelled,
        // network hiccup — should fall through to the SFSafariView
        // redirect flow rather than surface a raw error.
        // eslint-disable-next-line no-console
        console.warn('Native Google sign-in unavailable — falling back:', err?.message || err);
      }
      // iter885 · A second tap while Google's sheet is coming up would
      // close it and open a new one — ignore taps within 2.5s.
      if (Date.now() - _lastGoogleTap < 2500) return;
      _lastGoogleTap = Date.now();
      const explicit = process.env.REACT_APP_IOS_OAUTH_CALLBACK_URL;
      const publicOrigin = (process.env.REACT_APP_PUBLIC_ORIGIN
        || process.env.REACT_APP_BACKEND_URL
        || 'https://flythouse.com').replace(/\/+$/, '');
      const redirectUrl = explicit || `${publicOrigin}/auth/callback`;
      // iter344 · White-labeled path FIRST on native iOS so the Safari
      // sheet flashes accounts.google.com → flythouse.com (never
      // "emergent"). Web already had this branch; native was hard-
      // coded to auth.emergentagent.com. If the backend has custom
      // OAuth configured we route through Google directly.
      let cfg = null;
      try {
        const raw = window.sessionStorage.getItem('flyt_oauth_cfg');
        if (raw) cfg = JSON.parse(raw);
      } catch { /* ignore */ }
      if (!cfg) {
        try {
          const r = await userApi.get('/auth/google/config');
          cfg = r?.data || null;
          try { window.sessionStorage.setItem('flyt_oauth_cfg', JSON.stringify(cfg)); } catch { /* ignore */ }
        } catch { cfg = null; }
      }
      if (cfg?.enabled && cfg?.client_id) {
        // iter450 · App-tagged one-time state. iOS regularly finishes the
        // OAuth redirect INSIDE the SFSafariViewController (server-side
        // 302s don't trigger Universal Links), which used to strand the
        // session in Safari's cookie jar — the app never got a token, so
        // users looked logged-out on next launch. Now the backend stashes
        // the session token against this `app_` state during the code
        // exchange and we poll /auth/handoff/{state} below to claim it
        // no matter which surface completed the exchange.
        const state = `app_${(() => {
          try {
            const a = new Uint8Array(16);
            window.crypto.getRandomValues(a);
            return Array.from(a, (b) => b.toString(16).padStart(2, '0')).join('');
          } catch {
            return `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
          }
        })()}`;
        try { window.sessionStorage.setItem('flyt_oauth_state', state); } catch { /* ignore */ }
        let pendingSaved = false;
        try { window.localStorage.setItem(PENDING_GOOGLE_KEY, JSON.stringify({ state, at: Date.now() })); pendingSaved = true; } catch { /* ignore */ }
        const params = new URLSearchParams({
          client_id: cfg.client_id,
          redirect_uri: redirectUrl,
          response_type: 'code',
          scope: 'openid email profile',
          access_type: 'online',
          include_granted_scopes: 'true',
          prompt: 'select_account',
          state,
        });
        // iter880 · App Health journey tracking (fire-and-forget).
        const gJourney = journeyAttempt('login_google', state);
        let opened;
        try {
          opened = await openExternalAuth(`https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`);
        } catch (openErr) {
          clearPendingGoogle(state);
          gJourney.fail(openErr);
          throw openErr;
        }
        // iter885 · Opened in the Safari app instead of the in-app sheet —
        // the sign-in still completes; FLYT signs in when they come back.
        if (opened === 'safari') {
          try { toast.info('Pick your Google account in Safari, then come back to FLYT \u2014 you\u2019ll be signed in automatically.', { duration: 8000 }); } catch { /* ignore */ }
        }
        // Poll for the session handoff. Covers both completion surfaces:
        //  • Universal Link intercepted → in-app AuthCallback exchanged the
        //    code (and the backend still stashed the handoff) — claim is a
        //    harmless re-save of the same token.
        //  • Exchange finished inside the Safari sheet → this claim is the
        //    ONLY way the app learns its session token.
        const deadline = Date.now() + (opened === 'safari' ? 300_000 : 150_000);
        while (Date.now() < deadline) {
          await new Promise((r) => setTimeout(r, 1500));
          // Claimed on return-to-app (or superseded by a newer attempt).
          if (pendingSaved && readPendingGoogle()?.state !== state) return;
          try {
            if (await claimGoogleHandoff(state)) { gJourney.ok(); return; }
          } catch { /* network blip — keep polling */ }
        }
        return;
      }
      // iter310 · Force Google to show its account-picker screen (rather
      // than silently picking whatever email is autofilled from iCloud
      // Keychain). Fallback path if white-labeled OAuth isn't enabled.
      const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}&prompt=select_account`;
      const eJourney = journeyAttempt('login_google');
      try { window.sessionStorage.setItem('flyt_journey_google', eJourney.id); } catch { /* ignore */ }
      try {
        await openExternalAuth(authUrl);
      } catch (openErr) {
        eJourney.fail(openErr);
        throw openErr;
      }
      return;
    }
    // Web: check if custom OAuth is enabled. Cheap network call, cached
    // for the lifetime of the tab so tapping the button is instant on
    // subsequent attempts.
    let cfg = null;
    try {
      const raw = window.sessionStorage.getItem('flyt_oauth_cfg');
      if (raw) cfg = JSON.parse(raw);
    } catch { /* ignore */ }
    if (!cfg) {
      try {
        const r = await userApi.get('/auth/google/config');
        cfg = r?.data || null;
        try { window.sessionStorage.setItem('flyt_oauth_cfg', JSON.stringify(cfg)); } catch { /* ignore */ }
      } catch { cfg = null; }
    }
    if (cfg?.enabled && cfg?.client_id) {
      // White-labeled path — send them straight to Google.
      const redirectUri = window.location.origin + '/auth/google';
      const state = Math.random().toString(36).slice(2);
      try { window.sessionStorage.setItem('flyt_oauth_state', state); } catch { /* ignore */ }
      const params = new URLSearchParams({
        client_id: cfg.client_id,
        redirect_uri: redirectUri,
        response_type: 'code',
        scope: 'openid email profile',
        access_type: 'online',
        include_granted_scopes: 'true',
        prompt: 'select_account',
        state,
      });
      // iter880 · finished by GoogleAuthCallback via journeyResume.
      try { window.sessionStorage.setItem('flyt_journey_google', journeyAttempt('login_google').id); } catch { /* ignore */ }
      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
      return;
    }
    // Fallback → Emergent-managed flow.
    try { window.sessionStorage.setItem('flyt_journey_google', journeyAttempt('login_google').id); } catch { /* ignore */ }
    const redirectUrl = window.location.origin + returnPath;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);
  // iter304 · Post-auth attribution — called immediately after any
  // successful sign-in / sign-up so the ambassador who referred this
  // visitor gets credited. Reads the code from localStorage / cookie
  // and hits the authenticated /api/ambassador/attribute endpoint (the
  // backend is idempotent — safe to call twice). Silent no-op if there
  // is no code stashed.
  const applyReferralAttribution = useCallback(async () => {
    const code = readReferralCode();
    if (!code) return null;
    try {
      const r = await userApi.post('/ambassador/attribute', { code });
      if (r?.data?.ok) clearReferralCode();
      return r?.data || null;
    } catch (_) {
      // Non-fatal: user still signed in, just won't be attributed.
      return null;
    }
  }, []);
  const emailLogin = useCallback(async ({ email, password, remember = true, refCode = null }) => {
    if (refCode) stashReferralCode(refCode);
    // iter628 · Purge cross-account cache tiers BEFORE the fresh
    // session boots so the new user's first render never grabs a
    // stale response left behind by the previous account on this
    // device (Willie → Jaxon on the same phone, same WKWebView).
    try { window.sessionStorage?.clear?.(); } catch { /* ignore */ }
    try {
      if (typeof window !== 'undefined' && window.caches?.keys) {
        const keys = await window.caches.keys();
        await Promise.all(keys.map((k) => window.caches.delete(k)));
      }
    } catch { /* ignore */ }
    // iter880 · App Health journey tracking. Wrong password / unverified
    // / locked out count as the person's outcome, not a broken step.
    const j = journeyAttempt('login_email');
    let res;
    try {
      res = await userApi.post('/auth/email/login', { email, password, remember });
    } catch (e) { j.fail(e); throw e; }
    setUser(res.data.user);
    j.ok();
    // iter377 · Stamp the login count + push the email onto the MRU
    // suggestions list. Fire-and-forget — never blocks the sign-in.
    saveRecentEmail(email).catch(() => {});
    markLoginSuccess().catch(() => {});
    // iter293 · The login response is the raw public_users row — derived
    // fields (linked_athlete, is_founder, premium) are only computed by
    // /me hydration. Fetch it right away so founder/athlete controls
    // (e.g. the FLYT ID™ builder pill) appear WITHOUT a reload.
    try {
      const me = await userApi.get('/me');
      if (me?.data?.user_id) setUser(me.data);
    } catch { /* raw user already set — hydration is best-effort */ }
    // iter304 · Belated attribution for existing accounts — if the user
    // typed a referral code on the login form (or has one stashed from
    // an earlier `?ref=CODE` visit), credit the ambassador now.
    await applyReferralAttribution();
    return res.data.user;
  }, [applyReferralAttribution]);
  const emailSignup = useCallback(async ({ name, email, password, refCode = null, dob = null, guardianName = null, guardianEmail = null, guardianRelationship = null, termsAccepted = false, termsVersion = null }) => {
    const { default: inferSignupChannel } = await import('../utils/inferSignupChannel');
    if (refCode) stashReferralCode(refCode);
    const activeRef = readReferralCode();
    const payload = {
      name, email, password,
      signup_channel: inferSignupChannel(),
      ref_code: activeRef,
    };
    // iter316 · Optional minor-athlete fields. Backend age-gates:
    // < 13 → 403, 13-17 → guardian required, 18+ → proceed.
    if (dob) payload.dob = dob;
    if (guardianName) payload.guardian_name = guardianName;
    if (guardianEmail) payload.guardian_email = guardianEmail;
    if (guardianRelationship) payload.guardian_relationship = guardianRelationship;
    // iter588 · Terms of Service consent receipt (audit trail).
    if (termsAccepted) {
      payload.terms_accepted = true;
      payload.terms_version = termsVersion || 'v1';
    }
    // iter880 · 400 here = invalid input (password length, DOB) — the
    // person's outcome, not a broken sign-up.
    const j = journeyAttempt('signup_email', undefined, { userSideStatuses: [400] });
    let res;
    try {
      res = await userApi.post('/auth/email/signup', payload);
    } catch (e) { j.fail(e); throw e; }
    j.ok();
    // iter673 · New email accounts are created PENDING — no session is
    // returned until the 6-digit code is verified. Skip user/session
    // hydration and hand the pending flag back so the modal shows the
    // code-entry step.
    if (res.data?.pending_verification) {
      try {
        const { track } = await import('../lib/analytics');
        track('signup_pending_verification', { method: 'email', signup_channel: inferSignupChannel() });
      } catch (_) { /* analytics never breaks UX */ }
      return res.data;
    }
    setUser(res.data.user);
    // iter377 · Login count + MRU email suggestions for email signup.
    saveRecentEmail(email).catch(() => {});
    markLoginSuccess().catch(() => {});
    // iter304 · Attribute via the authenticated endpoint too so the
    // ambassador program's counters + milestones stay authoritative
    // even if the legacy /auth/email/signup handler doesn't wire it.
    await applyReferralAttribution();
    // PostHog signup event for funnel analysis.
    try {
      const { track } = await import('../lib/analytics');
      track('signup', { method: 'email', signup_channel: inferSignupChannel(), ref_code: activeRef, is_minor: !!res.data?.guardian_consent });
    } catch (_) { /* analytics never breaks UX */ }
    return res.data;   // return the full response so caller can surface guardian_consent / pending_verification
  }, [applyReferralAttribution]);
  // iter673 · Confirm the 6-digit email code → activates the account and
  // returns the now-authenticated user (session cookie set by backend).
  const verifyEmail = useCallback(async ({ email, code }) => {
    // iter880 · 400 = wrong / expired code typed — person's outcome.
    const j = journeyAttempt('verify_email', undefined, { userSideStatuses: [400, 410] });
    let res;
    try {
      res = await userApi.post('/auth/email/verify', {
        email: (email || '').trim().toLowerCase(),
        code: (code || '').trim(),
      });
    } catch (e) { j.fail(e); throw e; }
    setUser(res.data.user);
    j.ok();
    saveRecentEmail(email).catch(() => {});
    markLoginSuccess().catch(() => {});
    metaCompletedRegistration('email');
    trackMeta('CompleteRegistration', { email, customData: { method: 'email', status: 'complete' } });
    try {
      const me = await userApi.get('/me');
      if (me?.data?.user_id) setUser(me.data);
    } catch { /* raw user already set */ }
    await applyReferralAttribution();
    try { const { track } = await import('../lib/analytics'); track('email_verified', { method: 'email' }); } catch (_) { /* ignore */ }
    return res.data.user;
  }, [applyReferralAttribution]);
  // iter673 · Ask the backend to re-send a verification code. Generic
  // response either way (no email enumeration).
  const resendVerification = useCallback(async (email) => {
    const res = await userApi.post('/auth/email/resend-verification', {
      email: (email || '').trim().toLowerCase(),
    });
    return res.data;
  }, []);
  const exchangeSession = useCallback(async (sessionId) => {
    const { default: inferSignupChannel } = await import('../utils/inferSignupChannel');
    const activeRef = readReferralCode();
    const res = await userApi.post('/auth/google/session', {
      session_id: sessionId,
      signup_channel: inferSignupChannel(),
      ref_code: activeRef,
    });
    // iter310 · Persist the token IMMEDIATELY + synchronously here as
    // well as via the response interceptor. On native iOS the user often
    // swipes the app away seconds after the Safari sheet closes — if the
    // Preferences write is still in-flight when the JS runtime suspends,
    // the token never lands on disk and the next launch shows them as
    // signed-out. Two awaits (interceptor + here) guarantee it.
    if (res?.data?.session_token) {
      try { await saveToken(res.data.session_token); } catch { /* ignore */ }
    }
    setUser(res.data.user);
    // iter724 · The user opened LoginModal (loginOpen=true) to tap
    // "Continue with Google" in the first place, then the OAuth
    // redirect/callback completes on a completely different component
    // (AuthCallback.js) that has no idea LoginModal was ever open. Left
    // unset, loginOpen stayed stuck true for the rest of the session —
    // invisible only while PersonaWelcomeModal/AthleteOnboardingFlow/
    // FlytPaywall (all higher z-index) covered it, then reappearing as
    // "STEP INSIDE" on top of the real profile the moment the LAST one
    // (the paywall) closed. Close it explicitly now that auth is done.
    setLoginOpen(false);
    // iter377 · Login count + MRU email suggestions for Google OAuth.
    if (res?.data?.user?.email) saveRecentEmail(res.data.user.email).catch(() => {});
    markLoginSuccess().catch(() => {});
    if (res.data?.is_new_user) { metaCompletedRegistration('google'); trackMeta('CompleteRegistration', { email: res.data?.user?.email, customData: { method: 'google', status: 'complete' } }); }
    await applyReferralAttribution();
    try {
      const { track } = await import('../lib/analytics');
      track('signup', { method: 'google_oauth', signup_channel: inferSignupChannel(), ref_code: activeRef });
    } catch (_) { /* analytics never breaks UX */ }
    return res.data.user;
  }, [applyReferralAttribution]);
  // iter119 · White-labeled OAuth code exchange. The /auth/google
  // callback route calls this with the `code` + the exact redirect_uri
  // it used to start the flow. Google requires the redirect_uri to
  // match byte-for-byte or it returns invalid_grant.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
  // URLS, THIS BREAKS THE AUTH.
  const exchangeGoogleCode = useCallback(async (code, redirectUri, oauthState = null) => {
    const { default: inferSignupChannel } = await import('../utils/inferSignupChannel');
    const activeRef = readReferralCode();
    const res = await userApi.post('/auth/google/exchange', {
      code,
      redirect_uri: redirectUri,
      state: oauthState || undefined,
      signup_channel: inferSignupChannel(),
      ref_code: activeRef,
    });
    // iter366 · Persist the session token IMMEDIATELY + synchronously
    // to Capacitor Preferences on native iOS. Mirrors the same fix
    // `exchangeSession` got in iter310. Without this, when the user
    // swipes the app away seconds after Google signs them in, the
    // token never lands on disk and the next launch shows them as
    // signed-out — exact bug Jay hit in TestFlight.
    if (res?.data?.session_token) {
      try { await saveToken(res.data.session_token); } catch { /* ignore */ }
    }
    setUser(res.data.user);
    // iter724 · Same fix as exchangeSession above — close LoginModal
    // explicitly now that auth is done, since it can't close itself from
    // an OAuth callback happening on an unrelated component.
    setLoginOpen(false);
    // iter377 · Login count + MRU email suggestions for Google OAuth code exchange.
    if (res?.data?.user?.email) saveRecentEmail(res.data.user.email).catch(() => {});
    markLoginSuccess().catch(() => {});
    if (res.data?.is_new_user) { metaCompletedRegistration('google'); trackMeta('CompleteRegistration', { email: res.data?.user?.email, customData: { method: 'google', status: 'complete' } }); }
    await applyReferralAttribution();
    try {
      const { track } = await import('../lib/analytics');
      track('signup', { method: 'google_oauth', signup_channel: inferSignupChannel(), ref_code: activeRef });
    } catch (_) { /* analytics never breaks UX */ }
    return res.data.user;
  }, [applyReferralAttribution]);
  const logout = useCallback(async () => {
    try {
      await userApi.post('/auth/logout');
    } catch {
      // Best effort
    }
    setUser(null);
    // iOS: drop the persisted Bearer token too so the next visit is
    // truly signed-out. Also wipe the avatar backup so the next user
    // to sign in on this device doesn't inherit the previous user's
    // profile picture from Capacitor Preferences.
    try { await clearToken(); } catch { /* ignore */ }
    try { await clearAvatar(); } catch { /* ignore */ }
    try { await clearUserSnapshot(); } catch { /* ignore */ }
    // iter329 · Agent-persona image backups follow the same "clear on
    // logout" rule so the next signed-in user (fresh install / device
    // shared with a colleague) never inherits a previous agent's
    // cover photo or agency logo from Capacitor Preferences.
    try { await clearAgentCover(); } catch { /* ignore */ }
    try { await clearAgentPhoto(); } catch { /* ignore */ }
    // iter · Same ownership-scoped clear, for the Media Hub Home-Screen
    // widget — a shared/handed-down device must not keep showing the
    // previous account's Media Hub after they've signed out.
    try { await clearMediaHubWidgetAthlete(); } catch { /* ignore */ }
    // iter628 · Nuke every ownership-scoped browser cache tier so the
    // next account on this device (Willie → Jaxon on the same phone,
    // TestFlight ↔ App Store swap) can never render with the previous
    // user's owner state. Covers sessionStorage flags, Cache Storage
    // entries the service worker holds, and the WKWebView HTTP cache
    // via `caches.delete`.
    try { window.sessionStorage?.clear?.(); } catch { /* ignore */ }
    try {
      if (typeof window !== 'undefined' && window.caches?.keys) {
        const keys = await window.caches.keys();
        await Promise.all(keys.map((k) => window.caches.delete(k)));
      }
    } catch { /* ignore */ }
  }, []);
  const updateProfile = useCallback(async (patch) => {
    const res = await userApi.put('/me/profile', patch);
    setUser(res.data);
    // iter303 · Mirror the picture into Capacitor Preferences on native
    // iOS so the "avatar vanishes on relaunch" bug can never bite the
    // user — even if the server write silently drops the field, the
    // next boot rehydrates from this local copy AND re-PUTs to heal.
    // No-op on web (saveAvatar bails when not native).
    if (patch && Object.prototype.hasOwnProperty.call(patch, 'picture')) {
      const finalPic = res?.data?.picture || patch.picture || '';
      if (finalPic) {
        saveAvatar(finalPic).catch(() => {});
      } else {
        clearAvatar().catch(() => {});
      }
    }
    return res.data;
  }, []);
  const requireAuth = useCallback((cb) => {
    if (user) { cb?.(); return true; }
    setLoginOpen(true);
    return false;
  }, [user]);
  const toggleSave = useCallback(async (type, id) => {
    if (!user) { setLoginOpen(true); return null; }
    const res = await userApi.post(`/me/saved/${type}/${id}`);
    setUser((prev) => {
      if (!prev) return prev;
      const field = type === 'stories' ? 'saved_stories' : type === 'athletes' ? 'saved_athletes' : 'saved_videos';
      const current = prev[field] || [];
      const next = res.data.saved ? Array.from(new Set([...current, id])) : current.filter(x => x !== id);
      return { ...prev, [field]: next };
    });
    setSavedVersion((v) => v + 1);
    return res.data;
  }, [user]);
  // iter197 · Explicit follow toggle + green success/undo toast so
  // agents get visible confirmation on every tap (fixes the "I hit
  // follow but nothing happens" ambiguity). Bumps `savedVersion` so
  // ProfilePage's Following tab refetches the merged list right
  // away.
  const toggleFollow = useCallback(async (athleteId) => {
    if (!user) { setLoginOpen(true); return null; }
    try {
      const res = await userApi.post(`/me/follow/${athleteId}`);
      setUser((prev) => {
        if (!prev) return prev;
        const current = prev.following || [];
        const next = res.data?.following
          ? Array.from(new Set([...current, athleteId]))
          : current.filter((x) => x !== athleteId);
        return { ...prev, following: next };
      });
      setSavedVersion((v) => v + 1);
      return res.data;
    } catch (err) {
      const raw = err?.response?.data?.detail;
      // Dynamic import so the auth context doesn't drag `sonner`
      // into the bundle for every page.
      const { toast } = await import('sonner');
      toast.error(typeof raw === 'string' ? raw : 'Could not update follow. Try again.');
      throw err;
    }
  }, [user]);
  const isSaved = useCallback((type, id) => {
    if (!user) return false;
    const field = type === 'stories' ? 'saved_stories' : type === 'athletes' ? 'saved_athletes' : 'saved_videos';
    return (user[field] || []).includes(id);
  }, [user]);
  const fetchSaved = useCallback(async () => {
    if (!user) return { stories: [], athletes: [], videos: [] };
    // iter175 · Merge `saved_athletes[]` with `following[]` for the
    // Profile → Following tab. Save + Follow are independent taps
    // (per user request) but the profile surface should show any
    // athlete the user has flagged via EITHER action so nothing gets
    // "lost" between the two buckets.
    const [savedRes, followRes] = await Promise.all([
      userApi.get('/me/saved'),
      userApi.get('/me/following').catch(() => ({ data: { athletes: [] } })),
    ]);
    const saved = savedRes.data || {};
    const followed = (followRes.data && followRes.data.athletes) || [];
    const seen = new Set((saved.athletes || []).map((a) => a && a.id));
    const merged = [...(saved.athletes || [])];
    for (const a of followed) {
      if (a && a.id && !seen.has(a.id)) {
        seen.add(a.id);
        merged.push(a);
      }
    }
    return { ...saved, athletes: merged };
  }, [user]);
  const value = useMemo(() => ({
    user, setUser, loading,
    isAuthenticated: !!user,
    loginOpen, setLoginOpen, loginMode, setLoginMode, openSignup,
    startGoogleLogin, startAppleLogin, exchangeSession, exchangeGoogleCode, logout, refreshUser,
    emailLogin, emailSignup,
    verifyEmail, resendVerification,
    claimSheetOpen, setClaimSheetOpen,
    updateProfile, requireAuth,
    toggleSave, toggleFollow, isSaved, fetchSaved, savedVersion,
  }), [user, loading, loginOpen, loginMode, openSignup, startGoogleLogin, startAppleLogin, exchangeSession, exchangeGoogleCode, logout, refreshUser, emailLogin, emailSignup, verifyEmail, resendVerification, claimSheetOpen, updateProfile, requireAuth, toggleSave, toggleFollow, isSaved, fetchSaved, savedVersion]);
  return (
    <UserAuthContext.Provider value={value}>
      {children}
    </UserAuthContext.Provider>
  );
};
export const useUserAuth = () => {
  const ctx = useContext(UserAuthContext);
  if (!ctx) throw new Error('useUserAuth must be used within UserAuthProvider');
  return ctx;
};
